import * as style from "./DataDomain.module.scss";
import { Typography, IconButton } from "@mui/material";
import React, { useEffect, useState } from "react";
import { TextInput, Button, Divider } from "cprime-foundational-components";
import { triggerToast } from "../../utils/toast";
import RemoveCircleOutlineOutlinedIcon from "@mui/icons-material/RemoveCircleOutlineOutlined";
import {
  createDomain,
  deleteDomain,
  editDomain,
} from "../../services/RequestAccess/RequestAccess";
import { useLocation } from "react-router-dom";

interface ManageDataDomainProps {
  isEdit: boolean;
  appId: number;
  formData: any;
  onClose: any;
  incrementTableRefresh: any;
}

const ManageDataDomain: React.FC<ManageDataDomainProps> = ({
  isEdit,
  appId,
  formData,
  onClose,
  incrementTableRefresh,
}) => {
  const location = useLocation();
  const scssClass = style as any;
  const [domainForm, setDomainForm] = useState<any>({
    domain: "",
    subDomains: [""],
  });
  const [isSaveLoading, setIsSaveLoading] = useState(false);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const isAllDomainsPage = location.pathname === "/all-domains";
  useEffect(() => {
    if (isEdit) {
      // Parse subdomains from formData - could be array or comma-separated string
      let subDomains: string[];
      if (Array.isArray(formData?.subdomains)) {
        subDomains = formData.subdomains;
      } else if (
        typeof formData?.subdomains === "string" &&
        formData.subdomains.trim() !== ""
      ) {
        // Split comma-separated string and trim each value
        subDomains = formData.subdomains
          .split(",")
          .map((sd: string) => sd.trim())
          .filter((sd: string) => sd !== "");
      } else {
        subDomains = [""];
      }

      setDomainForm({
        ...domainForm,
        domain: formData?.domain_name,
        subDomains: subDomains.length > 0 ? subDomains : [""],
      });
    } else {
      setDomainForm({
        domain: "",
        subDomains: [""],
      });
    }
  }, [isEdit]);

  const handleChange = (field: string, value: string) => {
    setDomainForm({ ...domainForm, [field]: value });
  };

  const handleSubDomainChange = (index: number, value: string) => {
    const updatedSubDomains = [...domainForm.subDomains];
    updatedSubDomains[index] = value;
    setDomainForm({ ...domainForm, subDomains: updatedSubDomains });
  };

  const addSubDomain = () => {
    setDomainForm({
      ...domainForm,
      subDomains: [...domainForm.subDomains, ""],
    });
  };

  const removeSubDomain = (index: number) => {
    if (domainForm.subDomains.length > 1) {
      const updatedSubDomains = domainForm.subDomains.filter(
        (_: any, i: number) => i !== index,
      );
      setDomainForm({ ...domainForm, subDomains: updatedSubDomains });
    }
  };

  const handleSave = async () => {
    setIsSaveLoading(true);
    try {
      // Filter out empty subdomains
      const validSubDomains = domainForm.subDomains.filter(
        (sd: string) => sd.trim() !== "",
      );

      if (isEdit === false) {
        const body = {
          appId: isAllDomainsPage ? null : appId,
          domainName: domainForm?.domain,
          subdomains: validSubDomains,
        };

        try {
          const result = await createDomain(body);
          if (result?.status === "SUCCESS") {
            triggerToast(`${result?.message}`, "success");
            onClose();
            incrementTableRefresh?.();
          } else {
            triggerToast(`${result?.message}`, "error");
          }
        } catch (error) {
          triggerToast("Error Creating Data Domain", "error");
        }
      }
      if (isEdit === true) {
        if (!formData?.domain_id) {
          triggerToast("Something went wrong", "error");
          return;
        }
        const body = {
          domainId: formData?.domain_id,
          domainName: domainForm?.domain,
          subdomains: validSubDomains,
        };
        try {
          const result = await editDomain(body);
          if (result?.status === "SUCCESS") {
            triggerToast(`${result?.message}`, "success");
            onClose();
            incrementTableRefresh?.();
          } else {
            triggerToast(`${result?.message}`, "error");
          }
        } catch (error) {
          triggerToast("Error Updating Data Domain", "error");
        }
      }
    } finally {
      setIsSaveLoading(false);
    }
  };

  const handleDelete = async () => {
    if (isEdit === true) {
      if (!formData?.domain_id) {
        triggerToast("Something went wrong", "error");
        return;
      }
      setIsDeleteLoading(true);
      try {
        const result = await deleteDomain(formData?.domain_id);
        if (result?.status === "SUCCESS") {
          triggerToast(`${result?.message}`, "success");
          onClose();
          incrementTableRefresh?.();
        } else {
          triggerToast(`${result?.message}`, "error");
        }
      } catch (error) {
        triggerToast("Error deleting Data Domain", "error");
      } finally {
        setIsDeleteLoading(false);
      }
    }
  };

  const isFormValid = () => {
    const trimmedDomain = domainForm?.domain?.trim();

    // Domain must not be empty
    if (!trimmedDomain) return true;

    // Every subdomain must be non-empty
    const allSubDomainsFilled = domainForm.subDomains.every(
      (sd: string) => sd?.trim() !== "",
    );

    if (!allSubDomainsFilled) return true;

    // ---- Edit Mode Change Detection ----
    if (isEdit) {
      // Parse original subdomains - could be array or comma-separated string
      let originalSubDomains: string[];
      if (Array.isArray(formData?.subdomains)) {
        originalSubDomains = formData.subdomains;
      } else if (
        typeof formData?.subdomains === "string" &&
        formData.subdomains.trim() !== ""
      ) {
        originalSubDomains = formData.subdomains
          .split(",")
          .map((sd: string) => sd.trim())
          .filter((sd: string) => sd !== "");
      } else {
        originalSubDomains = [];
      }

      const trimmedCurrentSubDomains = domainForm.subDomains.map((sd: string) =>
        sd.trim(),
      );

      const domainUnchanged = trimmedDomain === formData?.domain_name?.trim();

      const subDomainsUnchanged =
        JSON.stringify(trimmedCurrentSubDomains) ===
        JSON.stringify(originalSubDomains);

      // Disable if nothing changed
      if (domainUnchanged && subDomainsUnchanged) return true;
    }

    // Enable Save
    return false;
  };

  return (
    <>
      <div className={scssClass.panelContent}>
        <Divider
          variant={"fullWidth"}
          sx={{
            marginBottom: "15px",
            width: "100%",
            marginLeft: "0px !important",
            backgroundColor: "#f8f8f9 !important",
          }}
        />
        <Typography className={scssClass.textDescription}>
          Domain Details
        </Typography>
        <div className={scssClass.fieldsContent}>
          <div className={scssClass.firstRow}>
            <TextInput
              value={domainForm?.domain}
              fullWidth
              sx={{
                width: "100%",
                "& .MuiInputBase-root": { height: "39.98px" },
              }}
              onChange={(e) => handleChange("domain", e?.target?.value)}
              placeholder="Enter Name"
              label="Domain Name"
            />
          </div>
          {domainForm.subDomains.map((subDomain: string, index: number) => (
            <div key={index} className={scssClass.secondRow}>
              <TextInput
                value={subDomain}
                fullWidth
                sx={{
                  width: "100%",
                  "& .MuiInputBase-root": { height: "39.98px" },
                }}
                onChange={(e) => handleSubDomainChange(index, e?.target?.value)}
                placeholder="Enter Name"
                label={`Attribute`}
              />
              {index > 0 && (
                <IconButton
                  onClick={() => removeSubDomain(index)}
                  sx={{
                    padding: "20px 1px 0px 16px",
                    "&:hover": { backgroundColor: "transparent" },
                  }}
                  aria-label="Remove subdomain"
                >
                  <RemoveCircleOutlineOutlinedIcon
                    sx={{ fontSize: "24px", color: "black" }}
                  />
                </IconButton>
              )}
            </div>
          ))}
          <div>
            <Button
              variant="tertiary"
              onClick={addSubDomain}
              sx={{
                fontSize: "14px",
                padding: "12px 16px",
                borderRadius: "4px",
                background: "#191A1C",
                color: "#FFF",
              }}
            >
              Add Attribute +
            </Button>
          </div>
        </div>
      </div>
      <div className={scssClass.bottomPanel}>
        <Divider
          variant="fullWidth"
          sx={{
            marginBottom: "15px",
            width: "100%",
            marginLeft: "0px !important",
            backgroundColor: "#f8f8f9 !important",
          }}
        />
        <div className={scssClass.footerButtons}>
          <div>
            {isEdit && (
              <Button
                variant="danger"
                loading={isDeleteLoading}
                onClick={handleDelete}
                disabled={isDeleteLoading || isSaveLoading}
              >
                Delete Domain
              </Button>
            )}
          </div>
          <div>
            <Button
              variant="tertiary"
              sx={{ marginRight: "10px" }}
              onClick={() => onClose?.()}
              disabled={isSaveLoading || isDeleteLoading}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              loading={isSaveLoading}
              sx={{ marginLeft: "5px" }}
              onClick={handleSave}
              disabled={isFormValid() || isSaveLoading || isDeleteLoading}
            >
              Save Domain
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ManageDataDomain;
